import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Star, Trash2, ArrowUpRight, ArrowDownRight, Sparkles, Lock,
  StickyNote, Tag, Save, X, Pencil, Clock, Search,
} from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import StockSearch from "@/components/StockSearch";
import WatchlistAddSearch from "@/components/WatchlistAddSearch";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtPrice, fmtPercentSigned } from "@/lib/format";
import { toast } from "sonner";
import { useTranslation } from "@/lib/i18n";

// Phase 8.LP.LR v1.6 — Clean, intentional display helpers for
// Watchlist rows whose live metrics are unavailable (search-resolved
// ETFs / less-known tickers outside the sync provider's universe).
//
// * `renderMoney`  → "$1,234.56" when finite, "—" otherwise (never "$–").
// * `renderScore`  → "72" when finite, "—" otherwise.
// * `hasFiniteNumber` → gates the upside arrow + color so a null
//     `upside_pct` doesn't paint a green "+—%" arrow row.
const EM_DASH = "\u2014"; // Prefer em-dash over hyphen for empty numerics.
function _isFinite(n) {
  return n !== null && n !== undefined && Number.isFinite(Number(n));
}
function renderMoney(n) {
  if (!_isFinite(n)) return EM_DASH;
  return `$${fmtPrice(n)}`;
}
function renderScore(n) {
  if (!_isFinite(n)) return EM_DASH;
  return String(n);
}
function renderUpside(n) {
  if (!_isFinite(n)) return EM_DASH;
  return fmtPercentSigned(n);
}
function hasFiniteNumber(n) {
  return _isFinite(n);
}
import WatchlistSortButton, { SortableHeader } from "@/components/watchlist/WatchlistSortButton";
import WatchlistFiltersButton, { ActiveFilterChips } from "@/components/watchlist/WatchlistFiltersButton";
import { useCompanyIdentities } from "@/lib/useCompanyIdentities";
import CompanyLogo from "@/components/common/CompanyLogo";
import {
  applyWatchlistSort,
  applyWatchlistFilters,
  formatRelativeAge,
  loadPersistedSort,
  persistSort,
  clearPersistedSort,
  DEFAULT_SORT_STATE,
  EMPTY_FILTER_STATE,
  isFilterActive,
} from "@/lib/watchlistSortFilter";

/** Curated Sector & themes palette (Phase 8.WLNAV.v1 — expanded to 24 tags).
 *  Codes are stable translation keys; emojis are the canonical brand-safe glyphs. */
const EMOJI_SECTORS = [
  { code: "ai",           emoji: "\uD83E\uDD16" },
  { code: "semis",        emoji: "\uD83D\uDCBE" },
  { code: "software",     emoji: "\uD83D\uDCBB" },
  { code: "cloud",        emoji: "\u2601\uFE0F" },
  { code: "cybersec",     emoji: "\uD83D\uDEE1\uFE0F" },
  { code: "banks",        emoji: "\uD83C\uDFE6" },
  { code: "fintech",      emoji: "\uD83D\uDCB3" },
  { code: "insurance",    emoji: "\uD83D\uDEE1" },
  { code: "healthcare",   emoji: "\uD83E\uDE7A" },
  { code: "biotech",      emoji: "\uD83E\uDDEC" },
  { code: "pharma",       emoji: "\uD83D\uDC8A" },
  { code: "energy",       emoji: "\u26A1" },
  { code: "clean_energy", emoji: "\uD83C\uDF31" },
  { code: "industrial",   emoji: "\uD83C\uDFED" },
  { code: "aerospace",    emoji: "\u2708\uFE0F" },
  { code: "auto",         emoji: "\uD83D\uDE97" },
  { code: "consumer",     emoji: "\uD83D\uDECD\uFE0F" },
  { code: "media",        emoji: "\uD83C\uDFAC" },
  { code: "telecom",      emoji: "\uD83D\uDCE1" },
  { code: "reits",        emoji: "\uD83C\uDFE2" },
  { code: "materials",    emoji: "\u26CF" },
  { code: "utilities",    emoji: "\uD83D\uDCA1" },
  { code: "space",        emoji: "\uD83D\uDE80" },
  { code: "crypto",       emoji: "\uD83E\uDE99" },
];

// First N sectors rendered inline in the editor; the rest are one click
// away via a "Show more" popover. Keeps the picker compact by default
// while never hiding the full palette.
const EDITOR_INLINE_SECTOR_COUNT = 8;

const SECTOR_EMOJI_TO_CODE = EMOJI_SECTORS.reduce((acc, s) => { acc[s.emoji] = s.code; return acc; }, {});

/** Default label suggestions (Phase 8.WLNAV.v1). English source-of-truth.
 *  Legacy labels ("Watching", "Earnings play", "Dividend", "Speculative",
 *  "Add on dip", "Trim on rally", "Thesis intact") are no longer offered as
 *  DEFAULTS but any user's existing saved labels remain intact and are
 *  displayed as user-owned custom labels. */
const SUGGESTED_LABELS = [
  "Long-term",
  "Earnings Watch",
  "Risk Flag",
  "High Conviction",
  "Dividend Candidate",
  "Research",
];

// Phase 8.WLNAV.v1 — cap on the number of Sector & themes chips per row.
const MAX_SECTORS_PER_ROW = 3;

export default function Watchlist() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(null);
  const [planKey, setPlanKey] = useState("free");
  const [latestDataAt, setLatestDataAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTicker, setEditingTicker] = useState(null);
  // Sort + filter state.
  //
  // Phase 8.WLNAV.v1.2 — sort is persisted PER-USER (see
  // watchlistSortFilter.js). Filters are session-only: they intentionally
  // reset to empty on every page load to prevent "hidden by filter"
  // confusion when the user returns after some time.
  //
  // First-time default (no saved preference): "Most recently added
  // first" (`added_at` DESC), with `ticker` as the stable secondary sort.
  const userScopedKey = user?.user_id || null;
  const [sort, setSort] = useState(() => loadPersistedSort(userScopedKey) || { ...DEFAULT_SORT_STATE });
  const [filters, setFilters] = useState(() => ({ ...EMPTY_FILTER_STATE }));
  // "Now" ticker so relative-age labels refresh every 30s without
  // requiring a full component re-fetch.
  const [nowTick, setNowTick] = useState(0);
  // Phase 8.WLNAV.v1.1 — tiny responsive helper for the deeper-analysis
  // input placeholder. Shorter copy on mobile keeps the input readable.
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const upd = () => setIsNarrow(mq.matches);
    upd();
    if (mq.addEventListener) { mq.addEventListener("change", upd); return () => mq.removeEventListener("change", upd); }
    mq.addListener(upd); return () => mq.removeListener(upd);
  }, []);
  const analysisPlaceholder = isNarrow
    ? (t("watchlist.analysis_placeholder_mobile") || "Analyze a stock")
    : (t("watchlist.analysis_placeholder") || "Search a stock to analyze");
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      setLoading(true);
      const res = await api.get("/watchlist");
      setItems(res.data.items || []);
      setCount(res.data.count ?? (res.data.items || []).length);
      setLimit(res.data.limit ?? null);
      setPlanKey(res.data.plan || "free");
      setLatestDataAt(res.data.latest_data_at || null);
    } catch {
      toast.error(t("watchlist.failed_load"));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (authLoading) return;
    // Phase 8.WLNAV.v1 — do not force-redirect logged-out visitors. The
    // Watchlist page renders a preview + Create-account/Log-in CTAs.
    if (!user) { setLoading(false); return; }
    refresh();
    // `refresh` is a stable, closure-captured helper defined above
    // this useEffect; adding it to the dep array would cause a
    // fetch loop on every render because it is recreated on each
    // call.  `user` is intentionally read only via `user?.user_id`
    // (a primitive) so identity churn on the user object never
    // retriggers the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.user_id]);

  // Persist sort changes to the per-user scoped storage key.
  //
  // The initial state was seeded synchronously from `loadPersistedSort`
  // above, but the auth context may resolve `user.user_id` a moment
  // later. When it does, re-check the stored preference for THIS user
  // and adopt it — this makes cross-tab / cross-session behavior
  // predictable: sign in and immediately see your saved sort.
  useEffect(() => {
    if (!userScopedKey) return;
    const saved = loadPersistedSort(userScopedKey);
    if (saved) setSort(saved);
    // Only run when user_id transitions from null → id (or changes).
  }, [userScopedKey]);
  useEffect(() => { persistSort(sort, userScopedKey); }, [sort, userScopedKey]);
  // Filters are intentionally NOT persisted in v1.2 (session-only).

  // Refresh relative-age labels every 30s without hitting the network.
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // Derived: label / sector universes for the filter panel. We take the
  // union of what appears on the user's rows so filters only expose
  // dimensions that actually match something.
  const availableLabels = useMemo(() => {
    const set = new Set();
    items.forEach((it) => (Array.isArray(it.labels) ? it.labels : []).forEach((l) => set.add(l)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const availableSectors = useMemo(() => {
    const set = new Set();
    items.forEach((it) => { if (it.sector_emoji) set.add(it.sector_emoji); });
    return Array.from(set).map((emoji) => ({ emoji, code: SECTOR_EMOJI_TO_CODE[emoji] || "custom" }));
  }, [items]);

  // Helper maps for the ActiveFilterChips row.
  const sectorLabelMap = useMemo(() => {
    const map = {};
    EMOJI_SECTORS.forEach((s) => {
      const trS = t(`watchlist.sectors.${s.code}`);
      map[s.emoji] = trS && trS !== `watchlist.sectors.${s.code}` ? trS : s.code;
    });
    return map;
  }, [t]);
  const labelDisplayMap = useMemo(() => {
    const map = {};
    availableLabels.forEach((l) => {
      const key = `watchlist.suggested_labels.${l}`;
      const trL = t(key);
      map[l] = trL && trL !== key ? trL : l;
    });
    return map;
  }, [availableLabels, t]);

  // Derived: view items (filtered + sorted). All client-side, never
  // triggers a re-fetch. `nowTick` is intentionally omitted from the
  // dependency array — its only purpose is to force the row-level age
  // labels to re-render, not to re-run the sort/filter.
  const viewItems = useMemo(() => {
    const filtered = applyWatchlistFilters(items, filters);
    return applyWatchlistSort(filtered, sort);
  }, [items, filters, sort]);

  const hasFilters = isFilterActive(filters);
  const hiddenCount = hasFilters ? Math.max(0, items.length - viewItems.length) : 0;

  // Phase 8.LP.LR — batched cache-only identity lookup for the
  // visible rows. Single GET /api/companies/identity request. Falls
  // back to initials when a ticker isn't cached, so no FMP call is
  // ever triggered from the Watchlist page.
  const viewTickers = useMemo(() => viewItems.map((it) => it.ticker), [viewItems]);
  const { getIdentity: getWatchlistIdentity } = useCompanyIdentities(viewTickers);

  const remove = async (ticker) => {
    try {
      await api.delete(`/watchlist/${ticker}`);
      setItems((prev) => prev.filter((i) => i.ticker !== ticker));
      setCount((c) => Math.max(0, c - 1));
      toast.success(t("watchlist.removed", { ticker }));
    } catch {
      toast.error(t("watchlist.failed_remove"));
    }
  };

  const isLimited = limit !== null;
  const atLimit = isLimited && count >= limit;
  const pct = isLimited && limit ? Math.min(100, Math.round((count / limit) * 100)) : 0;
  const canEditMeta = (user?.entitlements?.watchlist_notes && user?.entitlements?.watchlist_labels) || planKey === "pro" || planKey === "premium";

  const handleEditClick = (ticker) => {
    if (!canEditMeta) {
      toast.message(t("watchlist.pro_toast_title"), {
        description: t("watchlist.pro_toast_desc"),
        action: { label: t("watchlist.pro_toast_action"), onClick: () => navigate("/pricing") },
        duration: 6000,
      });
      return;
    }
    setEditingTicker((cur) => (cur === ticker ? null : ticker));
  };

  const onMetaSaved = (ticker, newMeta) => {
    setItems((prev) => prev.map((it) => (it.ticker === ticker ? { ...it, ...newMeta } : it)));
    setEditingTicker(null);
  };

  const remaining = isLimited ? Math.max(0, limit - count) : 0;

  // Phase 8.WLNAV.v1.1 — imperative helpers used by the empty-state
  // CTA and the "Analyze a stock instead" secondary link. Both are
  // purely local UI actions: no network call, no analysis quota,
  // no navigation off-page.
  const focusAddSearch = () => {
    const el = document.getElementById("watchlist-add-search");
    if (!el) return;
    try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* ignore */ }
    // Slight delay so the smooth-scroll doesn't steal focus mid-flight.
    setTimeout(() => { try { el.focus({ preventScroll: false }); } catch { /* ignore */ } }, 260);
  };
  const scrollToDeeperAnalysis = () => {
    const el = document.querySelector('[data-testid="watchlist-analysis-search-section"]');
    if (!el) return;
    try { el.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* ignore */ }
    // Also focus the analysis-search input for keyboard/screen-reader flow.
    setTimeout(() => {
      const input = el.querySelector('input[type="text"], input:not([type])');
      try { input && input.focus({ preventScroll: false }); } catch { /* ignore */ }
    }, 260);
  };

  // Click one of two sort actions:
  //   - Same key: toggle direction.
  //   - Different key: pick sensible default per column.
  const pickSort = (key) => {
    setSort((cur) => {
      if (cur.key === key) return { key, dir: cur.dir === "asc" ? "desc" : "asc" };
      const asc = ["ticker", "name", "status"].includes(key);
      return { key, dir: asc ? "asc" : "desc" };
    });
  };

  // Phase 8.WLNAV.v1 — logged-out preview state.
  if (!authLoading && !user) {
    return (
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 text-center" data-testid="watchlist-logged-out-preview">
          <Star className="w-10 h-10 mx-auto text-[#3FAE89]" />
          <h1 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight">{t("watchlist.loggedout_title") || "Build your Stockaily Watchlist"}</h1>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">{t("watchlist.loggedout_subtitle") || "Track price, Fair Value and AI Opportunity Scores in one place."}</p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="lg" onClick={() => navigate("/signup")} data-testid="watchlist-loggedout-signup">{t("watchlist.loggedout_signup") || "Create free account"}</Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/login?next=/watchlist")} data-testid="watchlist-loggedout-login">{t("watchlist.loggedout_login") || "Log in"}</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const latestAgeLabel = latestDataAt ? formatRelativeAge(latestDataAt, t) : null;

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="watchlist-header">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl tracking-tight">{t("watchlist.title")}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t("watchlist.subtitle")}</p>
          </div>
          <div className="w-full sm:w-[420px] max-w-full">
            {/* Phase 8.WLNAV.v1 — dedicated add-to-watchlist search.
                Does NOT navigate to /analyze. Does NOT call /api/analyze.
                Does NOT consume analysis quota. Adds the ticker via
                POST /api/watchlist and shows a success toast with an
                optional "Analyze now" secondary action. */}
            <WatchlistAddSearch
              onAdded={() => refresh()}
              onLimitHit={() => { /* toast is already surfaced */ }}
            />
          </div>
        </div>

        {/* Quota meter for free users */}
        {isLimited ? (
          <div
            className={`mt-5 rounded-2xl border bg-card/60 p-4 sm:px-5 sm:py-4 flex flex-col sm:flex-row sm:items-center gap-3 ${atLimit ? "border-[rgba(229,72,77,0.32)]" : "border-border/70"}`}
            data-testid="watchlist-quota-bar"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("watchlist.free_quota")}</div>
                <div className="font-mono tabular text-sm" data-testid="watchlist-quota-counter">
                  <span className={atLimit ? "text-[#E5484D]" : ""}>{count}</span>
                  <span className="text-muted-foreground"> / {limit}</span>
                </div>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-muted/40 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{
                  width: `${pct}%`,
                  background: atLimit ? "#E5484D" : pct >= 80 ? "#E0B341" : "#3FAE89",
                }} />
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                {atLimit
                  ? t("watchlist.at_limit")
                  : t(remaining === 1 ? "watchlist.add_more" : "watchlist.add_more_plural", { n: remaining })}
              </div>
            </div>
            <Button size="sm" variant={atLimit ? "default" : "outline"} onClick={() => navigate("/pricing")} data-testid="watchlist-upgrade-cta" className="shrink-0">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> {t("common.upgrade_pro")}
            </Button>
          </div>
        ) : (
          <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-border/70 bg-card/40 px-3 py-1 text-xs text-muted-foreground" data-testid="watchlist-unlimited-chip">
            <Sparkles className="w-3 h-3 text-[#3FAE89]" />
            {t("watchlist.unlimited_chip", { plan: planKey.charAt(0).toUpperCase() + planKey.slice(1) })}
          </div>
        )}

        {/* Controls row: Filters + Sort + Latest data */}
        {items.length > 0 ? (
          <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="watchlist-controls-row">
            <div className="flex items-center gap-2 flex-wrap">
              <WatchlistFiltersButton
                value={filters}
                onChange={setFilters}
                availableLabels={availableLabels}
                availableSectors={availableSectors}
                t={t}
              />
              {/* Sort button — always visible for parity; on desktop the
                  table headers are also clickable. */}
              <WatchlistSortButton
                sort={sort}
                onChange={setSort}
                onReset={() => {
                  clearPersistedSort(userScopedKey);
                  setSort({ ...DEFAULT_SORT_STATE });
                }}
                isDefault={sort.key === DEFAULT_SORT_STATE.key && sort.dir === DEFAULT_SORT_STATE.dir}
                t={t}
              />
            </div>
            <div
              className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"
              data-testid="watchlist-latest-data-chip"
              title={latestDataAt ? new Date(latestDataAt).toLocaleString() : undefined}
            >
              <Clock className="w-3 h-3" />
              {latestAgeLabel
                ? (<>
                    <span>{t("watchlist.freshness.latest_available_data") || "Latest available data"}:</span>
                    <span className="text-foreground font-medium">
                      {(t("watchlist.freshness.updated_ago", { age: latestAgeLabel }) || `${latestAgeLabel}`).replace("{age}", latestAgeLabel)}
                    </span>
                  </>)
                : (<>
                    <span>{t("watchlist.freshness.latest_available_data") || "Latest available data"}:</span>
                    <span className="text-foreground/80 font-medium">{t("watchlist.freshness.unavailable") || "Update time unavailable"}</span>
                  </>)
              }
            </div>
          </div>
        ) : null}

        <ActiveFilterChips
          value={filters}
          onChange={setFilters}
          t={t}
          sectorLabelMap={sectorLabelMap}
          labelDisplayMap={labelDisplayMap}
        />

        <div className="mt-5 rounded-2xl border border-border/70 bg-card/60 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground" data-testid="watchlist-loading">{t("common.loading")}</div>
          ) : items.length === 0 ? (
            <div className="p-8 sm:p-10 text-center" data-testid="watchlist-empty">
              <div className="mx-auto inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#3FAE89]/40 bg-[#3FAE89]/10 mb-3">
                <Star className="w-5 h-5 text-[#3FAE89]" />
              </div>
              <div className="font-display text-xl" data-testid="watchlist-empty-title">{t("watchlist.empty_title") || "Nothing saved yet"}</div>
              <p className="text-muted-foreground mt-2 max-w-sm mx-auto" data-testid="watchlist-empty-subtitle">
                {t("watchlist.empty_subtitle") || "Add your first stock to track price, Fair Value and AI Opportunity Score."}
              </p>
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2">
                <Button
                  onClick={focusAddSearch}
                  data-testid="watchlist-empty-cta"
                >
                  {t("watchlist.empty_cta") || "Add your first stock"}
                </Button>
                <button
                  type="button"
                  onClick={scrollToDeeperAnalysis}
                  data-testid="watchlist-empty-secondary-link"
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                >
                  {t("watchlist.empty_secondary") || "Analyze a stock instead"}
                </button>
              </div>
            </div>
          ) : viewItems.length === 0 ? (
            <div className="p-10 text-center" data-testid="watchlist-filtered-empty">
              <div className="font-display text-lg">{t("watchlist.filter.empty_title") || "No stocks match your filters"}</div>
              <p className="text-muted-foreground mt-2 text-sm">
                {t("watchlist.filter.empty_subtitle", { hidden: hiddenCount }) || `${hiddenCount} stock(s) hidden by filters.`}
              </p>
              <Button className="mt-4" size="sm" variant="outline" onClick={() => setFilters({ ...EMPTY_FILTER_STATE })} data-testid="watchlist-filtered-empty-reset">
                <X className="w-3.5 h-3.5 mr-1" /> {t("watchlist.filter.reset") || "Reset filters"}
              </Button>
            </div>
          ) : (
            <>
              {/* MOBILE: stacked cards (visible <md) */}
              <div className="md:hidden divide-y divide-border/60" data-testid="watchlist-mobile-list">
                {viewItems.map((it) => (
                  <React.Fragment key={it.id}>
                    <WatchlistMobileCard
                      item={it}
                      identity={getWatchlistIdentity(it.ticker)}
                      canEdit={canEditMeta}
                      t={t}
                      onAnalyze={() => navigate(`/analyze/${it.ticker}`)}
                      onEdit={() => handleEditClick(it.ticker)}
                      onRemove={() => remove(it.ticker)}
                    />
                    {editingTicker === it.ticker && canEditMeta ? (
                      <div data-testid="watchlist-editor-row" className="bg-secondary/30">
                        <MetaEditor
                          item={it}
                          onCancel={() => setEditingTicker(null)}
                          onSaved={onMetaSaved}
                          t={t}
                        />
                      </div>
                    ) : null}
                  </React.Fragment>
                ))}
              </div>

              {/* DESKTOP: full table (visible md+) */}
              <div className="hidden md:block overflow-x-auto">
              <Table data-testid="watchlist-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[160px]">
                      <SortableHeader sort={sort} sortKey="ticker" onSort={pickSort}>{t("watchlist.col_ticker")}</SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader sort={sort} sortKey="name" onSort={pickSort}>{t("watchlist.col_name")}</SortableHeader>
                    </TableHead>
                    <TableHead>{t("watchlist.col_notes")}</TableHead>
                    <TableHead className="text-right">
                      <SortableHeader sort={sort} sortKey="price" onSort={pickSort} align="right">{t("watchlist.col_price")}</SortableHeader>
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader sort={sort} sortKey="fair_value" onSort={pickSort} align="right">{t("watchlist.col_fair")}</SortableHeader>
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader sort={sort} sortKey="upside_pct" onSort={pickSort} align="right">{t("watchlist.col_upside")}</SortableHeader>
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader sort={sort} sortKey="ai_score" onSort={pickSort} align="right">{t("watchlist.col_score")}</SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader sort={sort} sortKey="status" onSort={pickSort}>{t("watchlist.col_status")}</SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader sort={sort} sortKey="market_data_updated_at" onSort={pickSort}>
                        {t("watchlist.col_updated") || "Updated"}
                      </SortableHeader>
                    </TableHead>
                    <TableHead className="text-right">{t("watchlist.col_actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {viewItems.map((it) => {
                    const wlIdent = getWatchlistIdentity(it.ticker);
                    return (
                    <React.Fragment key={it.id}>
                      <TableRow data-testid="watchlist-row" className="hover:bg-secondary/40 cursor-pointer" onClick={() => navigate(`/analyze/${it.ticker}`)}>
                        <TableCell className="font-mono">
                          <span className="inline-flex items-center gap-2">
                            {/* Phase 8.LP.LR — real logo when cached,
                                sector-emoji + text otherwise. */}
                            <CompanyLogo
                              ticker={it.ticker}
                              name={wlIdent.name || it.name}
                              logoUrl={wlIdent.logo_url}
                              size="xs"
                              testId={`watchlist-logo-${it.ticker}`}
                            />
                            {it.sector_emoji ? <span className="text-base leading-none" aria-hidden>{it.sector_emoji}</span> : null}
                            {it.ticker}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground max-w-[160px] truncate" data-testid={`watchlist-row-name-${it.ticker}`}>
                          {/* Phase 8.LP.LR v1.6 — Company column must
                              never be blank. Priority order:
                              1) row `name` (from provider or backend
                                 identity-cache merge),
                              2) identity-endpoint name (client cache),
                              3) ticker fallback. */}
                          {it.name || (wlIdent && wlIdent.name) || it.ticker}
                        </TableCell>
                        <TableCell>
                          <NotesAndTagsCell item={it} canEdit={canEditMeta} t={t} />
                        </TableCell>
                        <TableCell className="text-right tabular" data-testid={`watchlist-row-price-${it.ticker}`}>
                          <div className="flex flex-col items-end gap-0.5">
                            <PriceCell price={it.price} analysisAsOf={it.analysis_as_of} t={t} testId={`watchlist-row-price-value-${it.ticker}`} />
                            {(it.is_stale || (it.row_source === "watchlist_history" && it.analysis_as_of)) && (
                              <StaleBadge
                                isStale={!!it.is_stale}
                                analysisAsOf={it.analysis_as_of}
                                t={t}
                                testId={`watchlist-row-stale-${it.ticker}`}
                                compact={true}
                              />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular" data-testid={`watchlist-row-fair-${it.ticker}`}>{renderMoney(it.fair_value)}</TableCell>
                        <TableCell
                          className={`text-right tabular ${
                            hasFiniteNumber(it.upside_pct)
                              ? (it.upside_pct >= 0 ? "text-[#3FAE89]" : "text-[#E5484D]")
                              : "text-muted-foreground"
                          }`}
                          data-testid={`watchlist-row-upside-${it.ticker}`}
                        >
                          {hasFiniteNumber(it.upside_pct) ? (
                            <span className="inline-flex items-center gap-1 justify-end">
                              {it.upside_pct >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />} {renderUpside(it.upside_pct)}
                            </span>
                          ) : (
                            <span>{EM_DASH}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular" data-testid={`watchlist-row-score-${it.ticker}`}>{renderScore(it.ai_score)}</TableCell>
                        <TableCell><StatusBadge status={it.status} classification={it.classification} /></TableCell>
                        <TableCell className="text-[11px] text-muted-foreground">
                          <FreshnessCell iso={it.market_data_updated_at} t={t} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditClick(it.ticker)}
                              data-testid="watchlist-edit-button"
                              title={canEditMeta ? t("watchlist.edit_title_pro") : t("watchlist.edit_title_locked")}
                            >
                              {canEditMeta ? <Pencil className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => navigate(`/analyze/${it.ticker}`)} data-testid="watchlist-analyze-button">
                              <Star className="w-3.5 h-3.5 mr-1" /> {t("watchlist.btn_analyze")}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => remove(it.ticker)} data-testid="watchlist-remove-button">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {editingTicker === it.ticker && canEditMeta ? (
                        <TableRow data-testid="watchlist-editor-row-desktop" className="bg-secondary/30">
                          <TableCell colSpan={10} className="p-0">
                            <MetaEditor
                              item={it}
                              onCancel={() => setEditingTicker(null)}
                              onSaved={onMetaSaved}
                              t={t}
                            />
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
            </>
          )}
        </div>

        {/* Pro feature teaser for free users */}
        {planKey === "free" && items.length > 0 ? (
          <div
            className="mt-6 rounded-2xl border border-dashed border-border/70 bg-card/30 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
            data-testid="watchlist-pro-teaser"
          >
            <div className="flex-1 min-w-0 flex items-start gap-3">
              <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-secondary/60 border border-border/70 shrink-0">
                <Lock className="w-4 h-4 text-[#3182CE]" />
              </span>
              <div>
                <div className="text-sm font-medium">{t("watchlist.teaser_title")}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {t("watchlist.teaser_body")}
                </div>
              </div>
            </div>
            <Button size="sm" onClick={() => navigate("/pricing")} className="shrink-0" data-testid="watchlist-pro-teaser-cta">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> {t("common.upgrade_pro")}
            </Button>
          </div>
        ) : null}
      </main>
      {/* Phase 8.WLNAV.v1.1 — Deeper Analysis section BELOW the
          Watchlist. Clearly labeled as an analysis flow (uses the
          existing StockSearch which starts a full analysis, respects
          plan and quota rules). Restrained visual polish: slightly
          taller card, blue-green edge accent, small icon, wider input,
          eyebrow + supporting capability line. */}
      {user ? (
        <section
          className={`max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 ${items.length === 0 ? "pt-2 pb-10" : "pb-10"}`}
          data-testid="watchlist-analysis-search-section"
        >
          <div
            className="relative rounded-2xl border bg-card/45 p-5 sm:p-7"
            style={{
              // Subtle blue-green edge accent. Two-tone gradient border
              // achieved via border + a very light inset ring. Deliberately
              // low intensity to keep the "premium dark" aesthetic.
              borderColor: "rgba(63,174,137,0.24)",
              boxShadow: "inset 0 0 0 1px rgba(49,130,206,0.10)",
            }}
            data-testid="watchlist-analysis-search-card"
          >
            {/* Phase 8.LP.LR v1.7 — The card itself is `overflow-visible`
                so the StockSearch suggestion dropdown can extend
                outside the card boundary. The decorative corner glow
                still needs to be clipped to the rounded card shape,
                so it lives inside its own inset overflow-hidden
                layer that is *behind* interactive content
                (pointer-events-none, no z-index). */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden"
            >
              {/* Barely-visible corner glow (top-left). Restrained. */}
              <div
                aria-hidden
                className="absolute -top-16 -left-16 w-56 h-56 rounded-full opacity-[0.10]"
                style={{ background: "radial-gradient(closest-side, #3FAE89 0%, transparent 70%)" }}
              />
            </div>
            <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8 relative">
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  <span
                    aria-hidden
                    className="inline-flex items-center justify-center w-6 h-6 rounded-md border"
                    style={{ borderColor: "rgba(63,174,137,0.35)", background: "rgba(63,174,137,0.10)", color: "#3FAE89" }}
                  >
                    <Search className="w-3 h-3" />
                  </span>
                  {t("watchlist.analysis_section_kicker") || "DEEPER ANALYSIS"}
                </div>
                <h2 className="mt-2 font-display text-2xl sm:text-[26px] leading-tight tracking-tight">
                  {t("watchlist.analysis_section_title") || "Analyze any stock"}
                </h2>
                <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">
                  {t("watchlist.analysis_section_body") || "Get Fair Value, Opportunity Score, risk and AI insights in seconds."}
                </p>
                {/* Capability line — dot-separated pills, plain text
                    to keep the visual weight down. */}
                <div
                  className="mt-3 text-[11px] text-foreground/70 flex flex-wrap items-center gap-x-1 gap-y-1"
                  data-testid="watchlist-analysis-capability-line"
                >
                  {(t("watchlist.analysis_capabilities") || "Fair Value · Opportunity Score · Risk · AI Insights")
                    .split("·")
                    .map((chunk, idx, arr) => (
                      <React.Fragment key={idx}>
                        <span className="inline-block">{chunk.trim()}</span>
                        {idx < arr.length - 1 ? <span aria-hidden className="text-muted-foreground/50">·</span> : null}
                      </React.Fragment>
                    ))}
                </div>
              </div>
              <div className="w-full lg:w-[440px] lg:max-w-[50%]">
                <StockSearch
                  testIdPrefix="watchlist-analysis-search"
                  placeholder={analysisPlaceholder}
                />
              </div>
            </div>
          </div>
        </section>
      ) : null}
      <Footer />
    </div>
  );
}

/* ---------- Price cell with "as of analysis" tooltip ----------
 * Phase 8.LP.LR correction (Watchlist Price projection + truthful
 * timestamp).  The Watchlist Price column shows the price captured
 * by the stored analysis, not a live quote.  When ``analysis_as_of``
 * is present we attach an unobtrusive tooltip that makes this
 * explicit in DE/EN, so a cached snapshot is never implied to be a
 * live market quote.  When the row has no analysis (Branch B), we
 * fall back to the plain em-dash renderMoney output without a
 * tooltip. */
function PriceCell({ price, analysisAsOf, t, testId }) {
  const money = renderMoney(price);
  if (price === null || price === undefined || !analysisAsOf) {
    return <span data-testid={testId}>{money}</span>;
  }
  let whenShort = "";
  let whenAbs = "";
  try {
    const d = new Date(analysisAsOf);
    whenShort = formatRelativeAge(analysisAsOf, t);
    whenAbs = d.toLocaleString();
  } catch (e) {
    whenShort = "";
    whenAbs = analysisAsOf;
  }
  const inlineHint =
    (t("watchlist.col_price_as_of_analysis") || "as of analysis {time}")
      .replace("{time}", whenShort || whenAbs);
  const tooltipText =
    (t("watchlist.col_price_tooltip") ||
      "Price shown is from the saved analysis snapshot ({time}), not a live quote.")
      .replace("{time}", whenAbs);
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={testId}
            data-analysis-as-of={analysisAsOf}
            className="inline-flex items-baseline gap-1 cursor-help"
          >
            {money}
            <span
              className="text-[10px] text-muted-foreground"
              aria-label={tooltipText}
            >
              · {inlineHint}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/* ---------- Stale-analysis badge (desktop + mobile) ----------
 * Phase 8.LP.LR correction follow-up — the Watchlist row shows a
 * VISIBLE "Last analysis <time> · Stale" label when the backend
 * surfaces ``is_stale: true`` on the row (24+ hours since the
 * saved analysis).  The ``title`` attribute carries a localized
 * tooltip for accessibility and the ``<time>`` element preserves
 * the machine-readable timestamp so screen readers and automated
 * accessibility checks can resolve it. */
function StaleBadge({ isStale, analysisAsOf, t, testId, compact = false }) {
  if (!analysisAsOf) return null;
  let whenShort = "";
  let whenAbs = "";
  try {
    const d = new Date(analysisAsOf);
    whenShort = formatRelativeAge(analysisAsOf, t) || d.toLocaleString();
    whenAbs = d.toLocaleString();
  } catch (e) {
    whenShort = analysisAsOf;
    whenAbs = analysisAsOf;
  }
  const labelKey = isStale ? "watchlist.stale_label" : "watchlist.stale_label_fresh";
  const defaultText = isStale ? "Last analysis {time} · stale" : "Last analysis {time}";
  const line = (t(labelKey) || defaultText).replace("{time}", whenShort);
  const tooltip = t("watchlist.stale_tooltip") || "";
  const badgeText = t("watchlist.stale_badge") || "Stale";
  const colorCls = isStale
    ? "text-amber-700 dark:text-amber-300"
    : "text-muted-foreground";
  return (
    <span
      data-testid={testId}
      data-is-stale={isStale ? "1" : "0"}
      className={`inline-flex items-center gap-1 ${compact ? "text-[10px]" : "text-xs"} ${colorCls}`}
      title={isStale ? tooltip : whenAbs}
    >
      {isStale && (
        <span
          className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-50 px-1.5 py-[1px] text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-500/15 dark:text-amber-200"
          data-testid={`${testId}-chip`}
        >
          {badgeText}
        </span>
      )}
      <time dateTime={analysisAsOf}>{line}</time>
    </span>
  );
}

/* ---------- Freshness cell (desktop table) ---------- */
function FreshnessCell({ iso, t }) {
  if (!iso) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              data-testid="watchlist-freshness-unavailable"
              className="inline-flex items-center gap-1 opacity-70"
            >
              <Clock className="w-3 h-3" />
              {t("watchlist.freshness.unavailable_short") || "—"}
            </span>
          </TooltipTrigger>
          <TooltipContent>{t("watchlist.freshness.unavailable") || "Update time unavailable"}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  const age = formatRelativeAge(iso, t);
  const abs = new Date(iso).toLocaleString();
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="watchlist-freshness-cell"
            className="inline-flex items-center gap-1"
          >
            <Clock className="w-3 h-3" />
            {age}
          </span>
        </TooltipTrigger>
        <TooltipContent>{abs}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/* ---------- Mobile-friendly stacked card row ---------- */
function WatchlistMobileCard({ item, canEdit, t, onAnalyze, onEdit, onRemove, identity }) {
  const labels = Array.isArray(item.labels) ? item.labels : [];
  const note = item.notes || "";
  const tLabel = (l) => {
    const key = `watchlist.suggested_labels.${l}`;
    const tr = t(key);
    return tr === key ? l : tr;
  };
  const age = formatRelativeAge(item.market_data_updated_at, t);
  return (
    <div
      data-testid="watchlist-mobile-card"
      className="px-4 py-4 active:bg-secondary/40 transition-colors"
      role="button"
      tabIndex={0}
      onClick={onAnalyze}
      onKeyDown={(e) => { if (e.key === "Enter") onAnalyze(); }}
    >
      {/* Top row: ticker + status badge */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 flex items-center gap-2.5">
          {/* Phase 8.LP.LR — logo tile on the left. */}
          <CompanyLogo
            ticker={item.ticker}
            name={(identity && identity.name) || item.name}
            logoUrl={identity && identity.logo_url}
            size="sm"
            testId={`watchlist-mobile-logo-${item.ticker}`}
          />
          <div className="min-w-0 flex-1">
            <div className="font-mono text-base font-medium inline-flex items-center gap-1.5">
              {item.sector_emoji ? <span className="text-lg leading-none" aria-hidden>{item.sector_emoji}</span> : null}
              {item.ticker}
            </div>
            <div className="text-xs text-muted-foreground truncate mt-0.5" data-testid={`watchlist-mobile-name-${item.ticker}`}>
              {/* Phase 8.LP.LR v1.6 — Same name fallback chain as
                  the desktop table: row name → identity endpoint →
                  ticker. Never leaves a blank line. */}
              {item.name || (identity && identity.name) || item.ticker}
            </div>
          </div>
        </div>
        <StatusBadge status={item.status} />
      </div>

      {/* Numbers grid */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        <Stat label={t("watchlist.col_price")} value={
          <div className="flex flex-col items-start gap-0.5">
            <PriceCell price={item.price} analysisAsOf={item.analysis_as_of} t={t} testId={`watchlist-mobile-price-value-${item.ticker}`} />
            {(item.is_stale || (item.row_source === "watchlist_history" && item.analysis_as_of)) && (
              <StaleBadge
                isStale={!!item.is_stale}
                analysisAsOf={item.analysis_as_of}
                t={t}
                testId={`watchlist-mobile-stale-${item.ticker}`}
                compact={true}
              />
            )}
          </div>
        } testId={`watchlist-mobile-price-${item.ticker}`} />
        <Stat label={t("watchlist.col_fair")} value={renderMoney(item.fair_value)} testId={`watchlist-mobile-fair-${item.ticker}`} />
        <Stat
          label={t("watchlist.col_upside")}
          testId={`watchlist-mobile-upside-${item.ticker}`}
          value={hasFiniteNumber(item.upside_pct) ? (
            <span className={`inline-flex items-center gap-1 ${item.upside_pct >= 0 ? "text-[#3FAE89]" : "text-[#E5484D]"}`}>
              {item.upside_pct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {renderUpside(item.upside_pct)}
            </span>
          ) : (
            <span className="text-muted-foreground">{EM_DASH}</span>
          )}
        />
      </div>

      {/* Score + labels/notes preview */}
      <div className="mt-3 flex items-start gap-3">
        <div className="shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("watchlist.col_score")}</div>
          <div className="font-display text-2xl tabular leading-none mt-0.5" data-testid={`watchlist-mobile-score-${item.ticker}`}>{renderScore(item.ai_score)}</div>
        </div>
        <div className="flex-1 min-w-0">
          {labels.length ? (
            <div className="flex flex-wrap items-center gap-1">
              {labels.slice(0, 3).map((l) => (
                <span
                  key={l}
                  className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium border"
                  style={{ borderColor: "rgba(49,130,206,0.32)", color: "#3182CE", background: "rgba(49,130,206,0.10)" }}
                >
                  {tLabel(l)}
                </span>
              ))}
              {labels.length > 3 ? (
                <span className="text-[10px] text-muted-foreground">+{labels.length - 3}</span>
              ) : null}
            </div>
          ) : null}
          {note ? (
            <div className="text-[11px] text-foreground/80 line-clamp-2 mt-1" title={note}>{note}</div>
          ) : null}
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-muted-foreground/80" data-testid="watchlist-mobile-freshness">
            <Clock className="w-2.5 h-2.5" />
            {age
              ? ((t("watchlist.freshness.updated_ago", { age }) || `Updated ${age}`).replace("{age}", age))
              : (t("watchlist.freshness.unavailable") || "Update time unavailable")
            }
          </div>
        </div>
      </div>

      {/* Actions row */}
      <div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <Button
          size="sm"
          variant="outline"
          onClick={onEdit}
          className="h-9 px-3"
          data-testid="watchlist-edit-button"
          aria-label={canEdit ? t("watchlist.edit_title_pro") : t("watchlist.edit_title_locked")}
        >
          {canEdit ? <Pencil className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
        </Button>
        <Button size="sm" variant="outline" onClick={onAnalyze} className="h-9 flex-1" data-testid="watchlist-analyze-button">
          <Star className="w-3.5 h-3.5 mr-1" /> {t("watchlist.btn_analyze")}
        </Button>
        <Button size="sm" variant="ghost" onClick={onRemove} className="h-9 w-9 p-0" data-testid="watchlist-remove-button" aria-label="Remove">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-lg border border-border/60 bg-card/30 px-2 py-1.5" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono tabular text-xs sm:text-sm mt-0.5">{value}</div>
    </div>
  );
}

/* ---------- Notes & tags cell (read-only display in the table) ---------- */
function NotesAndTagsCell({ item, canEdit, t }) {
  const labels = Array.isArray(item.labels) ? item.labels : [];
  const note = item.notes || "";
  if (!labels.length && !note) {
    return (
      <div className="text-[11px] text-muted-foreground/70 inline-flex items-center gap-1.5">
        {canEdit ? (
          <>
            <Pencil className="w-3 h-3" /> {t("watchlist.no_notes")}
          </>
        ) : (
          <>
            <Lock className="w-3 h-3" /> {t("watchlist.pro_feature")}
          </>
        )}
      </div>
    );
  }
  // Translate stored labels via the suggested-labels dictionary if known.
  const tLabel = (l) => {
    const key = `watchlist.suggested_labels.${l}`;
    const tr = t(key);
    return tr === key ? l : tr;
  };
  return (
    <div className="space-y-1 max-w-[260px]">
      {labels.length ? (
        <div className="flex flex-wrap items-center gap-1">
          {labels.slice(0, 3).map((l) => (
            <span
              key={l}
              className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium border"
              style={{ borderColor: "rgba(49,130,206,0.32)", color: "#3182CE", background: "rgba(49,130,206,0.10)" }}
            >
              {tLabel(l)}
            </span>
          ))}
          {labels.length > 3 ? (
            <span className="text-[10px] text-muted-foreground">+{labels.length - 3}</span>
          ) : null}
        </div>
      ) : null}
      {note ? (
        <div className="text-[11px] text-foreground/80 line-clamp-2" title={note}>
          {note}
        </div>
      ) : null}
    </div>
  );
}

/* ---------- Sector picker (inline chips + "Show more" popover) ---------- */
/**
 * Renders the Sector & themes selector for the meta editor. Enforces
 * MAX_SECTORS_PER_ROW via the disabled/blocked feedback below the chip
 * grid. Existing single-emoji entries from earlier versions of the
 * schema continue to work (multi-select is stored as an array; the
 * legacy `sector_emoji` string is preserved for backward compatibility
 * by taking the first selected emoji as the canonical single value).
 */
function SectorPicker({ selected, onToggle, t, testIdPrefix = "watchlist-editor-emoji" }) {
  const [showMore, setShowMore] = useState(false);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const inline = EMOJI_SECTORS.slice(0, EDITOR_INLINE_SECTOR_COUNT);
  const rest = EMOJI_SECTORS.slice(EDITOR_INLINE_SECTOR_COUNT);
  const atMax = selected.length >= MAX_SECTORS_PER_ROW;
  const trSector = (code) => {
    const tr = t(`watchlist.sectors.${code}`);
    return tr && tr !== `watchlist.sectors.${code}` ? tr : code;
  };

  const renderChip = (s) => {
    const active = selected.includes(s.emoji);
    const disabled = !active && atMax;
    return (
      <button
        key={s.code}
        type="button"
        onClick={() => {
          if (disabled) return;
          onToggle(s.emoji);
        }}
        disabled={disabled}
        data-testid={`${testIdPrefix}-${s.code}`}
        data-active={active ? "true" : undefined}
        title={disabled ? (t("watchlist.sector.max_reached") || `Max ${MAX_SECTORS_PER_ROW} sectors`) : trSector(s.code)}
        className={`text-sm rounded-full pl-1.5 pr-2 py-0.5 border transition-colors ${active ? "" : disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-secondary/40"}`}
        style={{
          borderColor: active ? "rgba(49,130,206,0.55)" : "var(--border)",
          color: active ? "#3182CE" : "inherit",
          background: active ? "rgba(49,130,206,0.10)" : "transparent",
        }}
      >
        <span className="mr-1" aria-hidden>{s.emoji}</span>
        <span className="text-[10px] uppercase tracking-wider">{trSector(s.code)}</span>
      </button>
    );
  };

  return (
    <div>
      {/* Inline chips (first N). Rendered on desktop; on mobile the
          same chips are shown but an additional "Choose more" button
          opens a bottom sheet with the full palette. */}
      <div className="flex flex-wrap gap-1.5" data-testid="watchlist-sector-picker-inline">
        {inline.map(renderChip)}
        {/* Desktop "Show more" popover — hidden on <md via Tailwind. */}
        <div className="hidden md:inline-flex">
          <Popover open={showMore} onOpenChange={setShowMore}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="watchlist-sector-show-more"
                className="text-[11px] rounded-full px-2 py-1 border border-border/70 hover:bg-secondary/40"
              >
                {t("watchlist.sector.show_more") || `Show more (${rest.length})`}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[320px] max-h-[60vh] overflow-y-auto" data-testid="watchlist-sector-show-more-popover">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">{t("watchlist.sector.more_header") || "More sectors & themes"}</div>
              <div className="flex flex-wrap gap-1.5">
                {rest.map(renderChip)}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        {/* Mobile "Show more" opens Sheet — hidden on md+ via Tailwind. */}
        <div className="md:hidden inline-flex">
          <button
            type="button"
            data-testid="watchlist-sector-show-more-mobile"
            onClick={() => setMobileSheetOpen(true)}
            className="text-[11px] rounded-full px-2 py-1 border border-border/70 hover:bg-secondary/40"
          >
            {t("watchlist.sector.show_more") || `Show more (${rest.length})`}
          </button>
          <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
            <SheetContent side="bottom" className="rounded-t-2xl max-h-[80vh] overflow-y-auto" data-testid="watchlist-sector-mobile-sheet">
              <SheetHeader className="mb-4">
                <SheetTitle>{t("watchlist.sector.more_header") || "More sectors & themes"}</SheetTitle>
                <SheetDescription>{t("watchlist.sector.mobile_desc") || `Choose up to ${MAX_SECTORS_PER_ROW} tags.`}</SheetDescription>
              </SheetHeader>
              <div className="flex flex-wrap gap-1.5">
                {rest.map(renderChip)}
              </div>
              <div className="mt-4 flex justify-end">
                <Button size="sm" onClick={() => setMobileSheetOpen(false)}>{t("common.done") || "Done"}</Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      {/* Count / max feedback */}
      <div className="mt-2 flex items-center justify-between text-[10px]">
        <span
          data-testid="watchlist-sector-count"
          className={atMax ? "text-[#E0B341]" : "text-muted-foreground"}
        >
          {(t("watchlist.sector.count_of_max", { n: selected.length, max: MAX_SECTORS_PER_ROW }) || `${selected.length} / ${MAX_SECTORS_PER_ROW} selected`)
            .replace("{n}", String(selected.length))
            .replace("{max}", String(MAX_SECTORS_PER_ROW))}
        </span>
        {atMax ? (
          <span className="text-[#E0B341]" data-testid="watchlist-sector-max-reached">
            {t("watchlist.sector.max_reached_hint") || `Max ${MAX_SECTORS_PER_ROW} — deselect one to add another`}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/* ---------- Inline meta editor (Pro/Premium only) ---------- */
function MetaEditor({ item, onCancel, onSaved, t }) {
  const [notes, setNotes] = useState(item.notes || "");
  const [labels, setLabels] = useState(Array.isArray(item.labels) ? item.labels : []);
  // Backward compatible: legacy schema has a single `sector_emoji` string;
  // we still persist a single canonical value while allowing the picker
  // to record multiple selections via labels[] semantics below.
  // For now we treat `selectedSectors` as an ephemeral local set and
  // continue to write only the FIRST selected emoji to `sector_emoji`,
  // preserving the existing backend contract until the multi-sector
  // migration ships.
  const [selectedSectors, setSelectedSectors] = useState(
    item.sector_emoji ? [item.sector_emoji] : []
  );
  const [busy, setBusy] = useState(false);
  const [labelInput, setLabelInput] = useState("");

  const allLabels = useMemo(() => {
    const set = new Set([...labels, ...SUGGESTED_LABELS]);
    return Array.from(set);
  }, [labels]);

  const tLabel = (l) => {
    const key = `watchlist.suggested_labels.${l}`;
    const tr = t(key);
    return tr === key ? l : tr;
  };

  const toggleLabel = (l) => {
    setLabels((prev) => (prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]));
  };

  const addCustomLabel = () => {
    const v = labelInput.trim();
    if (!v) return;
    if (!labels.includes(v)) setLabels((p) => [...p, v]);
    setLabelInput("");
  };

  const toggleSector = (emoji) => {
    setSelectedSectors((prev) => {
      if (prev.includes(emoji)) return prev.filter((e) => e !== emoji);
      if (prev.length >= MAX_SECTORS_PER_ROW) return prev; // enforced also in SectorPicker
      return [...prev, emoji];
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      // Persist the first selected emoji as the canonical single value.
      // The multi-select UX is client-side only until the schema is
      // migrated to persist all selected sectors — see plan.md.
      const canonicalEmoji = selectedSectors[0] || null;
      const res = await api.patch(`/watchlist/${item.ticker}`, {
        notes,
        labels,
        sector_emoji: canonicalEmoji,
      });
      const next = res.data?.item || { notes, labels, sector_emoji: canonicalEmoji };
      onSaved(item.ticker, next);
      toast.success(t("watchlist.saved", { ticker: item.ticker }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail?.error === "feature_gated") {
        toast.message(t("watchlist.pro_toast_title"), {
          description: detail.message || t("watchlist.pro_toast_desc"),
        });
      } else {
        toast.error(t("watchlist.failed_save"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 py-4 sm:px-6 sm:py-5 grid grid-cols-1 lg:grid-cols-12 gap-5" onClick={(e) => e.stopPropagation()}>
      {/* Notes */}
      <div className="lg:col-span-5">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
          <StickyNote className="w-3.5 h-3.5" /> {t("watchlist.notes_label")}
        </div>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t("watchlist.notes_placeholder", { ticker: item.ticker })}
          rows={4}
          className="mt-2 resize-none"
          data-testid="watchlist-editor-notes"
        />
      </div>

      {/* Labels */}
      <div className="lg:col-span-4">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
          <Tag className="w-3.5 h-3.5" /> {t("watchlist.labels_label")}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {allLabels.map((l) => {
            const active = labels.includes(l);
            return (
              <button
                key={l}
                type="button"
                onClick={() => toggleLabel(l)}
                data-testid="watchlist-editor-label-toggle"
                data-active={active ? "true" : undefined}
                className={`text-[11px] rounded-full px-2 py-0.5 border transition-colors ${active ? "" : "hover:bg-secondary/40"}`}
                style={{
                  borderColor: active ? "rgba(49,130,206,0.55)" : "var(--border)",
                  color: active ? "#3182CE" : "inherit",
                  background: active ? "rgba(49,130,206,0.10)" : "transparent",
                }}
              >
                {tLabel(l)}
              </button>
            );
          })}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <input
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomLabel(); } }}
            placeholder={t("watchlist.labels_add_placeholder")}
            data-testid="watchlist-editor-label-input"
            className="flex-1 bg-transparent border border-border/70 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[color:var(--app-glow-blue)]"
          />
          <Button size="sm" variant="outline" onClick={addCustomLabel} data-testid="watchlist-editor-label-add">{t("common.add")}</Button>
        </div>
      </div>

      {/* Sector picker */}
      <div className="lg:col-span-3">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("watchlist.sector_label")}</div>
        <div className="mt-2">
          <SectorPicker
            selected={selectedSectors}
            onToggle={toggleSector}
            t={t}
          />
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel} data-testid="watchlist-editor-cancel">
            <X className="w-3.5 h-3.5 mr-1" /> {t("common.cancel")}
          </Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="watchlist-editor-save">
            <Save className="w-3.5 h-3.5 mr-1" /> {busy ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
